import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

const API = import.meta.env.VITE_API_BASE_URL;

const METRICS = ["temperature", "humidity", "co2"];

// Fetches reference data for one metric and returns shifted MetricRangeResponse points.
function useReferenceQuery({ growId, metric, enabled }) {
  return useQuery({
    queryKey: ["grow-reference", growId, metric],
    queryFn: async () => {
      const res = await fetch(`${API}grows/${growId}/reference?metric=${metric}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`reference fetch failed: ${res.status}`);
      return res.json();
    },
    enabled: !!growId && enabled,
    staleTime: 5 * 60_000,
  });
}

// Hook that fetches reference series for all three metrics when enabled.
// Returns { enabled, toggle, referenceSeriesFor }
//   referenceSeriesFor(metric) → array of ECharts series objects (dashed ghost lines)
export function useGrowReference(growId) {
  const [enabled, setEnabled] = useState(false);

  const tempRef  = useReferenceQuery({ growId, metric: "temperature", enabled });
  const humRef   = useReferenceQuery({ growId, metric: "humidity",    enabled });
  const co2Ref   = useReferenceQuery({ growId, metric: "co2",         enabled });

  const dataMap = { temperature: tempRef.data, humidity: humRef.data, co2: co2Ref.data };

  function referenceSeriesFor(metric) {
    const streams = dataMap[metric];
    if (!enabled || !streams) return [];
    return streams.map((s) => ({
      name: `${s.sensor || metric} (ref)`,
      type: "line",
      smooth: true,
      symbol: "none",
      lineStyle: { width: 1, type: "dashed", opacity: 0.5 },
      itemStyle: { opacity: 0.5 },
      areaStyle: null,
      data: s.points.map((p) => [p.t, p.v]),
      connectNulls: false,
    }));
  }

  const isLoading = enabled && (tempRef.isLoading || humRef.isLoading || co2Ref.isLoading);
  const hasError  = enabled && (tempRef.isError  || humRef.isError  || co2Ref.isError);

  return { enabled, toggle: () => setEnabled((v) => !v), referenceSeriesFor, isLoading, hasError };
}

// Toggle button UI shown in the GrowDetail chart header.
export default function GrowReferenceToggle({ enabled, onToggle, isLoading, hasError }) {
  return (
    <button
      onClick={onToggle}
      title="Compare against template grow"
      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border transition-all font-medium ${
        enabled
          ? "bg-violet-600 text-white border-violet-600"
          : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
      }`}
    >
      <span className="opacity-70">⚡</span>
      {isLoading ? "Loading…" : enabled ? "Reference on" : "Reference"}
      {hasError && <span className="text-red-300 ml-1">!</span>}
    </button>
  );
}
